import { useId, useRef } from "react";
import { twJoin } from "tailwind-merge";

import { ClapperboardIcon } from "@/components/Icons/ClapperboardIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { FileAttachment01Icon } from "@/components/Icons/FileAttachment01Icon";
import { Image01Icon } from "@/components/Icons/Image01Icon";
import { Send03Icon } from "@/components/Icons/Send03Icon";
import { Button } from "@/design-system/Button";
import { FileInput } from "@/design-system/FileInput";
import { Spinner } from "@/design-system/Spinner";
import { Tooltip } from "@/design-system/Tooltip";
import { ALLOWED_FILE_TYPES } from "@/helpers/file-types";
import { useTranslation } from "@/translations";

interface CommentFieldActionBaseProps {
  isSubmitting?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

interface CommentFieldAddImageActionProps extends CommentFieldActionBaseProps {
  onAddImages: (images: FileList) => void;
}

function CommentFieldAddImageAction({
  disabled,
  tooltip,
  isSubmitting,
  onAddImages,
}: CommentFieldAddImageActionProps): React.ReactNode {
  const id = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const onSelectImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    onAddImages(e.target.files);
    e.target.value = "";
  };
  let formattedTooltip: string | null = tooltip || t("component.comment-field.upload-image");
  if (disabled) {
    formattedTooltip = null;
  }

  return (
    <Tooltip tooltip={formattedTooltip}>
      <label
        className={twJoin(
          "relative mr-space-xs flex size-icon-prominent cursor-pointer items-center justify-center rounded-full transition-colors",
          disabled
            ? "opacity-30"
            : "focus-within:text-text-primary hover:bg-background-brand-light hover:text-text-primary focus:outline-hidden",
          isSubmitting && "pointer-events-none opacity-30",
        )}
        htmlFor={`comment-image-input-${id}`}
        data-testid="add-image-btn"
      >
        <FileInput
          data-testid="image-input"
          ref={fileInput}
          id={`comment-image-input-${id}`}
          name="img"
          accept={ALLOWED_FILE_TYPES.IMAGE}
          onChange={onSelectImage}
          disabled={disabled}
        />
        <Image01Icon size={20} />
      </label>
    </Tooltip>
  );
}

interface CommentFieldAddVideoActionProps extends CommentFieldActionBaseProps {
  onAddVideo: (video: FileList) => void;
}

function CommentFieldAddVideoAction({
  disabled,
  tooltip,
  isSubmitting,
  onAddVideo,
}: CommentFieldAddVideoActionProps): React.ReactNode {
  const id = useId();
  const { t } = useTranslation();
  const onSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    onAddVideo(event.target.files);
    event.target.value = "";
  };
  let formattedTooltip: string | null = tooltip || t("component.comment-field.upload-video");
  if (disabled) {
    formattedTooltip = null;
  }

  return (
    <Tooltip tooltip={formattedTooltip}>
      <label
        className={twJoin(
          "relative mr-space-xs flex size-icon-prominent cursor-pointer items-center justify-center rounded-full",
          disabled
            ? "opacity-30"
            : "transition-colors focus-within:text-text-primary hover:bg-background-brand-light hover:text-text-primary focus:outline-hidden",
          isSubmitting && "pointer-events-none opacity-30",
        )}
        htmlFor={`comment-video-input-${id}`}
        data-testid="add-video-btn"
      >
        <FileInput
          data-testid="video-input"
          id={`comment-video-input-${id}`}
          name="video"
          accept={ALLOWED_FILE_TYPES.VIDEO}
          onChange={onSelectFile}
          {...{
            disabled,
          }}
        />
        <ClapperboardIcon size={20} />
      </label>
    </Tooltip>
  );
}

interface CommentFieldAddDocumentActionProps extends CommentFieldActionBaseProps {
  onAddDocument: (document: FileList) => void;
}

function CommentFieldAddDocumentAction({
  disabled,
  tooltip,
  isSubmitting,
  onAddDocument,
}: CommentFieldAddDocumentActionProps): React.ReactNode {
  const id = useId();
  const { t } = useTranslation();
  const onSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    onAddDocument(event.target.files);
    event.target.value = "";
  };
  let formattedTooltip: string | null = tooltip || t("component.comment-field.upload-document");
  if (disabled) {
    formattedTooltip = null;
  }

  return (
    <Tooltip tooltip={formattedTooltip}>
      <label
        className={twJoin(
          "relative mr-space-xs flex size-icon-prominent cursor-pointer items-center justify-center rounded-full",
          disabled
            ? "opacity-30"
            : "transition-colors focus-within:text-text-primary hover:bg-background-brand-light hover:text-text-primary focus:outline-hidden",
          isSubmitting && "pointer-events-none opacity-30",
        )}
        htmlFor={`comment-document-input-${id}`}
        data-testid="add-document-btn"
      >
        <FileInput
          data-testid="document-input"
          id={`comment-document-input-${id}`}
          name="document"
          accept={ALLOWED_FILE_TYPES.DOCUMENT}
          onChange={onSelectFile}
          {...{
            disabled,
          }}
        />
        <FileAttachment01Icon size={20} />
      </label>
    </Tooltip>
  );
}

type CommentFieldSendActionProps = CommentFieldActionBaseProps & {
  isEdit?: boolean;
  disabled?: boolean;
  onSend: () => Promise<void>;
};

function CommentFieldSendAction({
  tooltip,
  isSubmitting,
  isEdit,
  disabled,
  onSend,
}: CommentFieldSendActionProps): React.ReactElement {
  const { t } = useTranslation();

  let IconSend = <Send03Icon size={20} />;
  if (isSubmitting) {
    IconSend = <Spinner size="sm" />;
  } else if (isEdit) {
    IconSend = <Edit05Icon size={20} />;
  }

  const actionLabel = tooltip || t("component.comment-field.send-comment");

  return (
    <Button
      variant="ghostPrimary"
      size="sm"
      shape="circular"
      title={actionLabel}
      aria-label={actionLabel}
      data-testid="send-comment"
      disabled={isSubmitting || disabled}
      onClick={onSend}
    >
      {IconSend}
    </Button>
  );
}

export const CommentFieldActions = {
  AddImage: CommentFieldAddImageAction,
  AddVideo: CommentFieldAddVideoAction,
  AddDocument: CommentFieldAddDocumentAction,
  Send: CommentFieldSendAction,
};
